import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const SESSION_KEY = "nv_hero_loaded_v1";
const HERO_IMAGE_URL =
  "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/b1b82944-5190-4b1a-b668-55a6f149be5a_compressed_hero-p01-shell-campaign-web.webp";
const MAX_TIMEOUT_MS = 2800;

export default function HeroLoader() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const alreadyLoaded =
    typeof window !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1";

  const [active, setActive] = useState(isHome && !alreadyLoaded);
  const [hiding, setHiding] = useState(false);
  const [progress, setProgress] = useState(8);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let progressTimer: ReturnType<typeof setInterval> | null = null;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      if (doneRef.current || cancelled) return;
      doneRef.current = true;
      setProgress(100);
      hideTimer = setTimeout(() => {
        if (cancelled) return;
        setHiding(true);
        setTimeout(() => {
          if (cancelled) return;
          setActive(false);
          try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
        }, 280);
      }, 160);
    };

    // Gradual crawl toward ~70%
    progressTimer = setInterval(() => {
      setProgress((p) => (p < 70 ? p + Math.max(1, (70 - p) * 0.08) : p));
    }, 90);

    // Hero image ready \u2192 push toward 85%
    const img = new Image();
    img.onload = () => {
      const done = () => !cancelled && setProgress((p) => Math.max(p, 85));
      if ("decode" in img) {
        img.decode().then(done).catch(done);
      } else {
        done();
      }
    };
    img.onerror = () => !cancelled && setProgress((p) => Math.max(p, 85));
    img.src = HERO_IMAGE_URL;

    // Weather Glass first frame \u2192 100%
    const onGlassReady = () => finish();
    window.addEventListener("nv:weatherglass-ready", onGlassReady);

    // Safety timeout
    const failsafe = setTimeout(finish, MAX_TIMEOUT_MS);

    return () => {
      cancelled = true;
      if (progressTimer) clearInterval(progressTimer);
      if (hideTimer) clearTimeout(hideTimer);
      clearTimeout(failsafe);
      window.removeEventListener("nv:weatherglass-ready", onGlassReady);
    };
  }, [active]);

  if (!active) return null;

  return (
    <div className={`nv-loader ${hiding ? "is-hiding" : ""}`} aria-hidden="true">
      <div className="nv-loader__label">NORTHVALE</div>
      <div className="nv-loader__track">
        <div className="nv-loader__bar" style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
    </div>
  );
}